import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button.tsx'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx'

type Status = 'loading' | 'ready' | 'submitting' | 'success' | 'already_unsubscribed' | 'error'

const REASONS = [
  'too_many_emails',
  'not_relevant',
  'no_longer_using',
  'other',
] as const

export function UnsubscribePage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<Status>('loading')
  const [selectedReason, setSelectedReason] = useState<string | null>(null)

  const token = searchParams.get('token')

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      setStatus('error')
      return
    }

    fetch(`/api/unsubscribe?token=${encodeURIComponent(token)}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.already_unsubscribed) {
          setStatus('already_unsubscribed')
        } else if (data.valid) {
          setStatus('ready')
        } else {
          setStatus('error')
        }
      })
      .catch(() => setStatus('error'))
  }, [token])

  const handleUnsubscribe = async () => {
    if (!token) return
    setStatus('submitting')

    try {
      const res = await fetch('/api/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          ...(selectedReason ? { reason: selectedReason } : {}),
        }),
      })
      const data = await res.json()

      if (data.success || data.already_unsubscribed) {
        setStatus('success')
      } else {
        setStatus('error')
      }
    } catch {
      setStatus('error')
    }
  }

  if (status === 'loading') {
    return (
      <PageWrapper>
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
            <p className="text-center mt-4 text-muted-foreground">
              {t('common.loading')}
            </p>
          </CardContent>
        </Card>
      </PageWrapper>
    )
  }

  if (status === 'error') {
    return (
      <PageWrapper>
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-12 h-12 rounded-lg bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <span className="text-destructive text-xl">&#x2717;</span>
            </div>
            <h2 className="text-xl font-semibold mb-2">{t('unsubscribe.errorTitle')}</h2>
            <p className="text-muted-foreground text-sm">{t('unsubscribe.errorDescription')}</p>
          </CardContent>
        </Card>
      </PageWrapper>
    )
  }

  if (status === 'already_unsubscribed') {
    return (
      <PageWrapper>
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-12 h-12 rounded-lg bg-[#63C36F] flex items-center justify-center mx-auto mb-4">
              <span className="text-white text-xl">&#x2713;</span>
            </div>
            <h2 className="text-xl font-semibold mb-2">{t('unsubscribe.alreadyTitle')}</h2>
            <p className="text-muted-foreground text-sm">{t('unsubscribe.alreadyDescription')}</p>
          </CardContent>
        </Card>
      </PageWrapper>
    )
  }

  if (status === 'success') {
    return (
      <PageWrapper>
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-12 h-12 rounded-lg bg-[#63C36F] flex items-center justify-center mx-auto mb-4">
              <span className="text-white text-xl">&#x2713;</span>
            </div>
            <h2 className="text-xl font-semibold mb-2">{t('unsubscribe.successTitle')}</h2>
            <p className="text-muted-foreground text-sm mb-2">{t('unsubscribe.successDescription')}</p>
            <p className="text-muted-foreground text-xs">{t('unsubscribe.criticalNote')}</p>
          </CardContent>
        </Card>
      </PageWrapper>
    )
  }

  // status === 'ready' — show reasons + unsubscribe button together
  return (
    <PageWrapper>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <div className="w-12 h-12 rounded-lg bg-[#63C36F] flex items-center justify-center mx-auto mb-3">
            <span className="text-white text-xl">&#x2713;</span>
          </div>
          <CardTitle className="text-xl">{t('unsubscribe.confirmTitle')}</CardTitle>
          <p className="text-muted-foreground text-sm mt-1">
            {t('unsubscribe.confirmDescription')}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {REASONS.map((reason) => (
            <button
              key={reason}
              onClick={() => setSelectedReason(reason)}
              className={`w-full flex items-center justify-between px-4 py-3.5 rounded-md border-2 text-sm transition-colors ${
                selectedReason === reason
                  ? 'border-primary text-primary'
                  : 'border-border hover:border-primary/40'
              }`}
            >
              <span>{t(`unsubscribe.reasons.${reason}`)}</span>
              {selectedReason === reason && (
                <span>&#x2713;</span>
              )}
            </button>
          ))}

          <Button
            className="w-full mt-4"
            onClick={handleUnsubscribe}
            disabled={status === 'submitting'}
          >
            {selectedReason
              ? t('unsubscribe.submitWithFeedback')
              : t('unsubscribe.confirmButton')}
          </Button>
        </CardContent>
      </Card>
    </PageWrapper>
  )
}

function PageWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#E8E4F0] to-[#D6E5F0] p-4 sm:p-8">
      {children}
    </div>
  )
}
